import React, { useContext, useEffect, useState } from 'react';
import {
  GridRowModel,
  GridRowModes,
  GridRowModesModel,
  useGridApiRef,
} from '@mui/x-data-grid-pro';
import { UIContext } from '../../../../context/UIContext';
import { deepClone } from '../../../../helpers/helpers';
import { CategoryForList } from './types';

const SidekickTablesContext = React.createContext({
  handleAddClick: (_id) => {
    /* */
  },
  handleDuplicateClick: (_id) => {
    /* */
  },
  handleEditClick: (_id) => {
    /* */
  },
  handleSaveClick: (_id) => {
    /* */
  },
  handleCancelClick: (_id) => {
    /* */
  },
  handleDeleteClick: (_id) => {
    /* */
  },
  processRowUpdate: (_newRow: GridRowModel, _oldRow: GridRowModel) => {
    /* */
  },
  handleRowModesModelChange: (_newRowModesModel: GridRowModesModel) => {
    /* */
  },
  handleProcessRowUpdateError: (_err: Error) => {
    /* */
  },
  isLoading: false,
  data: null,
  rowModesModel: {},
  rows: [],
  callApiFunction: (_id?: number) => {
    /* */
  },
  EditComponent: undefined as React.ComponentType<{
    currentObject: unknown;
    resetObject: () => void;
  }>,
  objectBeingEdited: undefined,
  resetCurrentEditObject: () => {
    /* */
  },
  updateObject: (_newObject: unknown) => {
    /* */
  },
  gridApiRef: null,
  categories: [],
  objectIdLoading: null,
});

const SidekickTablesContextProvider = <T, TNewObject>({
  data,
  children,
  apiFunction,
  isLoading,
  EditComponent,
  idKey,
  categories,
  setIsEditSidebarOpen,
}: {
  data: T[];
  children: JSX.Element;
  apiFunction: (rows: T[], id?: number) => void;
  isLoading: boolean;
  EditComponent: React.ComponentType<{
    currentObject: T;
    resetObject: () => void;
  }>;
  idKey: string;
  categories?: CategoryForList[];
  setIsEditSidebarOpen?: React.Dispatch<React.SetStateAction<boolean>>;
}) => {
  const [rows, setRows] = useState(data);
  const [rowModesModel, setRowModesModel] = React.useState<GridRowModesModel>({});
  const [objectBeingEdited, setObjectBeingEdited] = useState<T | undefined>(undefined);
  const { isMobile } = useContext(UIContext);
  const [rowEditId, setRowEditId] = useState<number>(null);
  const gridApiRef = useGridApiRef();
  const [objectIdLoading, setObjectIdLoading] = useState(null);

  const callApiFunction = (id?: number) => {
    if (isLoading || data?.length === 0) return;
    if (id != null) setObjectIdLoading(id);
    apiFunction(rows, id);
  };

  const handleAddClick = (objectId: number) => {
    callApiFunction(objectId);
  };

  const handleDuplicateClick = (id: number) => {
    const newRow = deepClone(rows.find((row) => row[idKey] === id));
    const newRows = deepClone(rows);
    newRows.splice(id, 0, newRow);
    setRows(newRows.map((row, index) => ({ ...row, id: index })));
  };

  const handleEditClick = (objectId: number) => {
    if (isMobile) {
      if (rowEditId) {
        // if already editing something, close that
        setRowEditId(null);
      }

      if (objectId !== rowEditId) {
        setRowEditId(objectId);
        setIsEditSidebarOpen(true);
        setObjectBeingEdited(rows.find((object) => object[idKey] === objectId));
      }
    } else {
      setRowModesModel({ ...rowModesModel, [objectId]: { mode: GridRowModes.Edit } });
    }
  };

  const handleSaveClick = (objectId: number) => {
    setRowModesModel({ ...rowModesModel, [objectId]: { mode: GridRowModes.View } });
  };

  const handleDeleteClick = (id: number) => {
    const newRows = rows.filter((row) => row[idKey] !== id);
    setRows(newRows.map((row, index) => ({ ...row, id: index })));
  };

  const handleCancelClick = (objectId: number) => {
    setRowModesModel({
      ...rowModesModel,
      [objectId]: { mode: GridRowModes.View, ignoreModifications: true },
    });
  };

  useEffect(() => {
    setRows(data);
  }, [data]);

  const processRowUpdate = (newRow: GridRowModel, oldRow: GridRowModel) => {
    setRows(rows.map((row) => (row[idKey] === newRow[idKey] ? (newRow as T) : row)));
    return newRow;
  };

  const handleRowModesModelChange = (newRowModesModel: GridRowModesModel) => {
    setRowModesModel(newRowModesModel);
  };

  const handleProcessRowUpdateError = (err: Error) => {
    console.log(err);
  };

  const resetCurrentEditObject = () => {
    if (rowEditId != null) {
      setRowEditId(null);
    }
    setIsEditSidebarOpen(false);
    setObjectBeingEdited(undefined);
  };

  const updateObject = (newObject: TNewObject) => {
    setRows(
      rows.map((row) => (row[idKey] === newObject[idKey] ? ({ ...row, ...newObject } as T) : row))
    );
    resetCurrentEditObject();
  };

  useEffect(() => {
    if (!isLoading) setObjectIdLoading(null);
  }, [isLoading]);

  const contextValue = {
    handleAddClick,
    handleDuplicateClick,
    handleEditClick,
    handleSaveClick,
    handleDeleteClick,
    handleCancelClick,
    processRowUpdate,
    handleRowModesModelChange,
    handleProcessRowUpdateError,
    isLoading,
    data,
    rowModesModel,
    rows,
    callApiFunction,
    EditComponent,
    objectBeingEdited,
    resetCurrentEditObject,
    updateObject,
    gridApiRef,
    categories,
    objectIdLoading,
  };

  return (
    <SidekickTablesContext.Provider value={contextValue}>{children}</SidekickTablesContext.Provider>
  );
};

export { SidekickTablesContext, SidekickTablesContextProvider };
